import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingBag, Globe, ChevronDown, User, Heart, LogOut } from 'lucide-react';
import useStore from '../store/useStore';
import { translations } from '../i18n/translations';
import AuthModal from './AuthModal';
import { getCategories } from '../api/api';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [shopDropdown, setShopDropdown] = useState(false);
  const [userDropdown, setUserDropdown] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const location = useLocation();
  const { language, setLanguage, getCartCount, isAuthenticated, user, logout, initAuth, wishlist, siteSettings } = useStore();
  const t = translations[language];
  const cartCount = getCartCount();

  useEffect(() => {
    initAuth();
  }, []);

  // ============================================================
  //  MODIFICATION : Colle ton URL Cloudinary complète ci-dessous
  // ============================================================
  const MY_CLOUDINARY_LOGO = "https://res.cloudinary.com/darsizywc/image/upload/v1769301672/logos/bzi7ffdtg8oe76hsodn6.png";
  // ============================================================

  const [categories, setCategories] = useState([
    { key: 'sweaters', path: '/shop/sweaters' },
    { key: 'hoodies', path: '/shop/hoodies' },
    { key: 'tshirts', path: '/shop/tshirts' },
    { key: 'jackets', path: '/shop/jackets' },
  ]);

  // Load categories from API
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await getCategories();
        if (data && Array.isArray(data) && data.length > 0) {
          // Filter to only active categories and map to navbar format
          const activeCats = data
            .filter(cat => cat.active !== false)
            .map(cat => {
              if (cat.slug) {
                // New format from db.categories
                return { 
                  key: cat.slug, 
                  path: `/shop/${cat.slug}`,
                  label: cat.translations?.[language]?.name || cat.translations?.fr?.name || cat.slug
                };
              } else if (cat.category) {
                // Legacy format from products aggregation
                return { key: cat.category, path: `/shop/${cat.category}` };
              }
              return null;
            })
            .filter(Boolean);
          
          if (activeCats.length > 0) {
            setCategories(activeCats);
          }
        }
      } catch (error) {
        console.error('Error loading categories for navbar:', error);
        // Keep default categories on error
      }
    };
    loadCategories();
  }, [language]);

  const isActive = (path) => location.pathname === path;

  const openAuth = (mode) => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  return (
    <>
      <nav className="bg-white shadow-md sticky top-0 z-50 print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo Section Modifiée */}
            <div className="flex items-center">
              <Link to={user?.role === 'admin' ? '/admin' : '/shop'} className="flex items-center space-x-2">
                <img 
                  src={MY_CLOUDINARY_LOGO} 
                  alt={siteSettings?.shop_name || 'EBB-SHOP'}
                  style={{ 
                    width: 'auto',         // Garde les proportions du logo
                    maxHeight: '40px'      // Ajuste la hauteur si besoin (40px est la taille standard actuelle)
                  }} 
                  className="object-contain"
                />
                <span className="text-xl font-bold text-amber-900">{siteSettings?.shop_name || 'EBB-SHOP'}</span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6">
              {/* Home - Admin uniquement */}
              {user?.role === 'admin' && (
                <Link
                  to="/home"
                  className={`text-sm font-medium transition-colors ${
                    isActive('/home') ? 'text-amber-700' : 'text-gray-700 hover:text-amber-700'
                  }`}
                >
                  {t.nav.home}
                </Link>
              )}

              {/* Shop Dropdown */}
              <div
                className="relative group"
                onMouseEnter={() => setShopDropdown(true)}
                onMouseLeave={() => setShopDropdown(false)}
              >
                <button
                  className={`flex items-center text-sm font-medium transition-colors py-4 ${
                    location.pathname.includes('/shop') ? 'text-amber-700' : 'text-gray-700 hover:text-amber-700'
                  }`}
                >
                  {t.nav.shop}
                  <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${shopDropdown ? 'rotate-180' : ''}`} />
                </button>
                {shopDropdown && (
                  <div className="absolute top-full left-0 w-48 bg-white shadow-lg rounded-md py-2 border border-gray-100">
                    {/* Invisible bridge to prevent gap issues */}
                    <div className="absolute -top-2 left-0 right-0 h-2" />
                    <Link
                      to="/shop"
                      onClick={() => setShopDropdown(false)}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700"
                    >
                      {t.products.allProducts}
                    </Link>
                    {categories.map((cat) => (
                      <Link
                        key={cat.key}
                        to={cat.path}
                        onClick={() => setShopDropdown(false)}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700"
                      >
                        {cat.label || t.nav[cat.key] || cat.key}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* About - Admin uniquement */}
              {user?.role === 'admin' && (
                <Link
                  to="/about"
                  className={`text-sm font-medium transition-colors ${
                    isActive('/about') ? 'text-amber-700' : 'text-gray-700 hover:text-amber-700'
                  }`}
                >
                  {t.nav.about}
                </Link>
              )}

              <Link
                to="/contact"
                className={`text-sm font-medium transition-colors ${
                  isActive('/contact') ? 'text-amber-700' : 'text-gray-700 hover:text-amber-700'
                }`}
              >
                {t.nav.contact}
              </Link>

              <Link
                to="/size-guide"
                className={`text-sm font-medium transition-colors ${
                  isActive('/size-guide') ? 'text-amber-700' : 'text-gray-700 hover:text-amber-700'
                }`}
              >
                {t.nav.sizeGuide}
              </Link>
            </div>

            {/* Right side */}
            <div className="flex items-center space-x-4">
              {/* Language Switcher */}
              <button
                onClick={() => setLanguage(language === 'fr' ? 'nl' : 'fr')}
                className="flex items-center space-x-1 text-sm text-gray-600 hover:text-amber-700"
              >
                <Globe className="w-4 h-4" />
                <span className="uppercase font-medium">{language}</span>
              </button>

              {/* User Menu - Toujours affiché (utilisateur forcément connecté) */}
              {isAuthenticated && (
                <div
                  className="relative"
                  onMouseEnter={() => setUserDropdown(true)}
                  onMouseLeave={() => setUserDropdown(false)}
                >
                  <button className="flex items-center gap-2 text-gray-600 hover:text-amber-700">
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-amber-700" />
                    </div>
                    <span className="hidden lg:block text-sm font-medium">
                      {user?.first_name}
                    </span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  {userDropdown && (
                    <div className="absolute top-full right-0 w-48 bg-white shadow-lg rounded-md py-2 mt-1">
                      <Link
                        to="/account"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700"
                      >
                        <User className="w-4 h-4" />
                        {t.account.title}
                      </Link>
                      <Link
                        to="/account"
                        onClick={() => {}}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700"
                      >
                        <Heart className="w-4 h-4" />
                        {t.account.wishlist}
                        {wishlist.length > 0 && (
                          <span className="ml-auto bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">
                            {wishlist.length}
                          </span>
                        )}
                      </Link>
                      <hr className="my-2" />
                      {/* Admin - uniquement pour les admins */}
                      {user?.role === 'admin' && (
                        <>
                          <Link
                            to="/admin"
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700"
                          >
                            {t.nav.admin}
                          </Link>
                          <hr className="my-2" />
                        </>
                      )}
                      <button
                        onClick={() => {
                          logout();
                          setUserDropdown(false);
                          window.location.href = '/login';
                        }}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <LogOut className="w-4 h-4" />
                        {t.auth.logout}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Cart */}
              <Link
                to="/cart"
                className="relative text-gray-600 hover:text-amber-700"
              >
                <ShoppingBag className="w-6 h-6" />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-amber-700 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Link>

              {/* Mobile menu button */}
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="md:hidden text-gray-600 hover:text-amber-700"
              >
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-3 space-y-2">
              {/* Home - Admin uniquement */}
              {user?.role === 'admin' && (
                <Link
                  to="/home"
                  onClick={() => setIsOpen(false)}
                  className="block py-2 text-gray-700 hover:text-amber-700"
                >
                  {t.nav.home}
                </Link>
              )}
              <Link
                to="/shop"
                onClick={() => setIsOpen(false)}
                className="block py-2 text-gray-700 hover:text-amber-700"
              >
                {t.nav.shop}
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.key}
                  to={cat.path}
                  onClick={() => setIsOpen(false)}
                  className="block py-2 pl-4 text-sm text-gray-600 hover:text-amber-700"
                >
                  {cat.label || t.nav[cat.key] || cat.key}
                </Link>
              ))}
              {/* About - Admin uniquement */}
              {user?.role === 'admin' && (
                <Link
                  to="/about"
                  onClick={() => setIsOpen(false)}
                  className="block py-2 text-gray-700 hover:text-amber-700"
                >
                  {t.nav.about}
                </Link>
              )}
              <Link
                to="/contact"
                onClick={() => setIsOpen(false)}
                className="block py-2 text-gray-700 hover:text-amber-700"
              >
                {t.nav.contact}
              </Link>
              <Link
                to="/size-guide"
                onClick={() => setIsOpen(false)}
                className="block py-2 text-gray-700 hover:text-amber-700"
              >
                {t.nav.sizeGuide}
              </Link>
              {isAuthenticated && (
                <>
                  <Link
                    to="/account"
                    onClick={() => setIsOpen(false)}
                    className="block py-2 text-gray-700 hover:text-amber-700"
                  >
                    {t.account.title}
                  </Link>
                  {/* Admin - uniquement pour les admins */}
                  {user?.role === 'admin' && (
                    <Link
                      to="/admin"
                      onClick={() => setIsOpen(false)}
                      className="block py-2 text-amber-700 font-medium hover:text-amber-800"
                    >
                      {t.nav.admin}
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      logout();
                      setIsOpen(false);
                    }}
                    className="block py-2 text-red-600"
                  >
                    {t.auth.logout}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode={authMode}
      />
    </>
  );
};

export default Navbar;